import { BillingTriggerType } from "../../types/api.model";
import { makeMaxCharges, makeOnApprovalBilling, makeOneTimeManualBilling, makeOngoingBilling, makeRepeatableManualBilling } from "./makers";
import { RecurrencePeriodTypes } from "./model";
import { BillingDayOfMonthModel } from "../BillingDayOfMonth";
import { defaultBillingDayOfWeek, defaultBillingDayOfYear } from "../../constants";
import * as selectors from "./selectors";
import { OneTimeInStagesModel } from "../OneTimeInStagesModel";
export function fromDTO(dto, isAwaitingApproval) {
  const {
    trigger,
    recurrencePeriod,
    billingDayOfMonth,
    billingDayOfWeek,
    isBilledUpfront,
    billUpfrontState,
    isPaused,
    isProrated,
    maxCharges
  } = dto;
  switch (trigger) {
    case 'onApproval':
    case BillingTriggerType.OnApproval:
      return makeOnApprovalBilling();
    case BillingTriggerType.OneTimeManual:
      return makeOneTimeManualBilling();
    case BillingTriggerType.RepeatableManual:
      if (recurrencePeriod !== '' && recurrencePeriod != null) return makeRepeatableManualBilling(recurrencePeriod, {
        billingDayOfMonth: billingDayOfMonth && BillingDayOfMonthModel.make(billingDayOfMonth),
        billingDayOfWeek,
        isBilledUpfront,
        maxCharges
      });else return null;
    case BillingTriggerType.Ongoing:
      if (recurrencePeriod !== '' && recurrencePeriod != null) return makeOngoingBilling(recurrencePeriod, {
        billingDayOfMonth: billingDayOfMonth && BillingDayOfMonthModel.make(billingDayOfMonth),
        billingDayOfWeek,
        isBilledUpfront,
        billUpfrontState: billUpfrontState != null ? billUpfrontState : undefined,
        isProrated,
        isPaused,
        maxCharges,
        isAwaitingApproval
      });else return null;
    case BillingTriggerType.OneTimeInStages:
      return {
        trigger: BillingTriggerType.OneTimeInStages,
        oneTimeInStages: OneTimeInStagesModel.fromDTO(dto.oneTimeInStages)
      };
  }
}
export function toDTO(billing) {
  switch (billing.trigger) {
    case BillingTriggerType.OnApproval:
      return {
        trigger: BillingTriggerType.OnApproval,
        billingDayOfMonth: null,
        billingDayOfWeek: null,
        billingDayOfYear: null,
        isBilledUpfront: false,
        isPaused: false,
        isProrated: false,
        maxCharges: makeMaxCharges(false),
        recurrencePeriod: ''
      };
    case BillingTriggerType.OneTimeManual:
      return {
        trigger: BillingTriggerType.OneTimeManual,
        billingDayOfMonth: null,
        billingDayOfWeek: null,
        billingDayOfYear: null,
        isBilledUpfront: false,
        isPaused: false,
        isProrated: false,
        maxCharges: makeMaxCharges(false),
        recurrencePeriod: ''
      };
    case BillingTriggerType.Ongoing:
      return {
        trigger: BillingTriggerType.Ongoing,
        billingDayOfYear: billing.billingDayOfYear,
        isBilledUpfront: billing.isBilledUpfront,
        billUpfrontState: billing.isBilledUpfront ? billing.billUpfrontState : null,
        recurrencePeriod: billing.recurrencePeriod,
        billingDayOfMonth: billing.billingDayOfMonth ? billing.billingDayOfMonth.toJSON() : null,
        billingDayOfWeek: billing.billingDayOfWeek,
        isProrated: billing.isProrated,
        isPaused: billing.isPaused,
        maxCharges: billing.maxCharges
      };
    case BillingTriggerType.RepeatableManual:
      return {
        trigger: BillingTriggerType.RepeatableManual,
        billingDayOfMonth: billing.billingDayOfMonth ? billing.billingDayOfMonth.toJSON() : null,
        billingDayOfWeek: billing.billingDayOfWeek,
        billingDayOfYear: billing.billingDayOfYear,
        isBilledUpfront: billing.isBilledUpfront,
        recurrencePeriod: billing.recurrencePeriod,
        maxCharges: billing.maxCharges,
        isPaused: false,
        isProrated: false
      };
    case BillingTriggerType.OneTimeInStages:
      return {
        trigger: BillingTriggerType.OneTimeInStages,
        billingDayOfMonth: null,
        billingDayOfWeek: null,
        billingDayOfYear: null,
        isBilledUpfront: false,
        isPaused: false,
        isProrated: false,
        maxCharges: makeMaxCharges(false),
        recurrencePeriod: '',
        oneTimeInStages: billing.oneTimeInStages.toDTO()
      };
  }
}
export function toDBServiceBillingTrigger(billing) {
  switch (billing.trigger) {
    case BillingTriggerType.OnApproval:
      return {
        type: BillingTriggerType.OnApproval
      };
    case BillingTriggerType.OneTimeManual:
      return {
        type: BillingTriggerType.OneTimeManual
      };
    case BillingTriggerType.Ongoing:
      return {
        type: BillingTriggerType.Ongoing,
        ongoing: {
          billUpfront: billing.isBilledUpfront,
          billUpfrontState: billing.billUpfrontState,
          isPaused: billing.isPaused,
          recurrence: toDBRecurrenceData(billing),
          prorateFirstInvoice: billing.isProrated,
          maxCharges: {
            enabled: selectors.maxChargesEnabled(billing),
            limit: selectors.maxChargesLimit(billing),
            chargesLeft: selectors.maxChargesLeft(billing)
          }
        }
      };
    case BillingTriggerType.RepeatableManual:
      return {
        type: BillingTriggerType.RepeatableManual,
        repeatableManual: {
          billUpfront: billing.isBilledUpfront,
          recurrence: toDBRecurrenceData(billing),
          maxCharges: {
            enabled: selectors.maxChargesEnabled(billing),
            limit: selectors.maxChargesLimit(billing),
            chargesLeft: selectors.maxChargesLeft(billing)
          }
        }
      };
    case BillingTriggerType.OneTimeInStages:
      return {
        type: BillingTriggerType.OneTimeInStages,
        oneTimeInStages: billing.oneTimeInStages.toDTO()
      };
  }
}
function toDBRecurrenceData(billing) {
  const {
    recurrencePeriod
  } = billing;
  const billingDayOfMonth = billing.billingDayOfMonth || BillingDayOfMonthModel.make();
  const billingDayOfWeek = billing.billingDayOfWeek || defaultBillingDayOfWeek;
  const billingDayOfYear = billing.billingDayOfYear || defaultBillingDayOfYear;
  return {
    period: {
      type: recurrencePeriod,
      monthly: recurrencePeriod === RecurrencePeriodTypes.Monthly ? {
        dayOfMonth: billingDayOfMonth.toDBType()
      } : null,
      weekly: recurrencePeriod === RecurrencePeriodTypes.Weekly ? {
        day: billingDayOfWeek
      } : null,
      biweekly: recurrencePeriod === RecurrencePeriodTypes.BiWeekly ? {
        day: billingDayOfWeek
      } : null,
      yearly: recurrencePeriod === RecurrencePeriodTypes.Yearly ? {
        dayOfYear: billingDayOfYear
      } : null
    }
  };
}